/** @format */

import React, { useState } from "react";
import { useWithdrawals } from "../hooks/useWithdrawals";
import { useSimpleUsers } from "../hooks/useSimpleUsers";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  UserIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import { copyToClipboard, getCopyButtonStyles } from "../utils/copyUtils";
import toast from "react-hot-toast";

const WithdrawalsPage = () => {
  const {
    withdrawals,
    loading,
    error,
    approveWithdrawal,
    rejectWithdrawal,
    deleteWithdrawal,
  } = useWithdrawals();
  const { users } = useSimpleUsers();
  const { currentUser } = useAuth();

  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedField, setCopiedField] = useState(null);

  // Filter withdrawals based on status
  const filteredWithdrawals = withdrawals.filter((withdrawal) => {
    if (statusFilter === "all") return true;
    return withdrawal.status === statusFilter;
  });

  // Get user details for a withdrawal
  const getUserDetails = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user || { name: "Unknown User", email: "Unknown" };
  };

  // Handle approval
  const handleApprove = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setConfirmAction("approve");
    setShowConfirmDialog(true);
  };

  // Handle rejection
  const handleReject = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowRejectionModal(true);
  };

  // Handle deletion
  const handleDelete = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setConfirmAction("delete");
    setShowConfirmDialog(true);
  };

  // Execute confirmed action
  const executeAction = async () => {
    if (!selectedWithdrawal) return;

    try {
      if (confirmAction === "approve") {
        await approveWithdrawal(selectedWithdrawal.id, currentUser.uid);
      } else if (confirmAction === "delete") {
        await deleteWithdrawal(selectedWithdrawal.id);
      }

      setShowConfirmDialog(false);
      setSelectedWithdrawal(null);
      setConfirmAction(null);
    } catch (error) {
      console.error("Error executing action:", error);
    }
  };

  // Execute rejection
  const executeRejection = async () => {
    if (!selectedWithdrawal || !rejectionReason.trim()) return;

    try {
      await rejectWithdrawal(
        selectedWithdrawal.id,
        currentUser.uid,
        rejectionReason
      );
      setShowRejectionModal(false);
      setSelectedWithdrawal(null);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
    }
  };

  // Copy text to clipboard
  const handleCopy = async (text, fieldName) => {
    const success = await copyToClipboard(text, fieldName);

    if (success) {
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard!`);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    } else {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: ClockIcon,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        text: "Pending",
      },
      approved: {
        icon: CheckCircleIcon,
        color: "bg-green-100 text-green-800 border-green-200",
        text: "Approved",
      },
      rejected: {
        icon: XCircleIcon,
        color: "bg-red-100 text-red-800 border-red-200",
        text: "Rejected",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
      >
        <IconComponent className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  // Get method badge
  const getMethodBadge = (method) => {
    const methodConfig = {
      mobile_money: {
        color: "bg-blue-100 text-blue-800 border-blue-200",
        text: "Mobile Money",
      },
      bank_transfer: {
        color: "bg-purple-100 text-purple-800 border-purple-200",
        text: "Bank Transfer",
      },
      paypal: {
        color: "bg-indigo-100 text-indigo-800 border-indigo-200",
        text: "PayPal",
      },
      crypto: {
        color: "bg-orange-100 text-orange-800 border-orange-200",
        text: "Cryptocurrency",
      },
      check: {
        color: "bg-gray-100 text-gray-800 border-gray-200",
        text: "Check",
      },
    };

    const config = methodConfig[method] || methodConfig.mobile_money;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">
            Error loading withdrawals: {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Withdrawal Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Manage user withdrawal requests and approvals
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 sm:px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">
                Pending
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {withdrawals.filter((w) => w.status === "pending").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">
                Approved
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {withdrawals.filter((w) => w.status === "approved").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">
                Rejected
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {withdrawals.filter((w) => w.status === "rejected").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CurrencyDollarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">
                Total Amount
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                $
                {withdrawals
                  .reduce((sum, w) => sum + (w.amount || 0), 0)
                  .toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawals Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Withdrawal Requests
          </h3>
        </div>

        {filteredWithdrawals.length === 0 ? (
          <div className="text-center py-12">
            <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No withdrawals found</p>
            <p className="text-gray-400 text-sm">
              When users submit withdrawal requests, they will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWithdrawals.map((withdrawal) => {
                    const user = getUserDetails(withdrawal.userId);
                    return (
                      <tr key={withdrawal.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-primary-600" />
                              </div>
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name}
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-500">
                                  {user.email}
                                </div>
                                {user.email && user.email !== "Unknown" && (
                                  <button
                                    onClick={() =>
                                      handleCopy(user.email, "Email")
                                    }
                                    className={getCopyButtonStyles(
                                      "Email",
                                      copiedField
                                    )}
                                    title="Copy email"
                                  >
                                    <ClipboardDocumentIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-900">
                              ${withdrawal.amount?.toFixed(2) || "0.00"}
                            </div>
                            {withdrawal.amount && (
                              <button
                                onClick={() =>
                                  handleCopy(
                                    withdrawal.amount.toFixed(2),
                                    "Amount"
                                  )
                                }
                                className={getCopyButtonStyles(
                                  "Amount",
                                  copiedField
                                )}
                                title="Copy amount"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {getMethodBadge(withdrawal.method)}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 space-y-2">
                            {/* Account Holder Name */}
                            {withdrawal.accountDetails?.name && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                                  <span className="font-medium">
                                    {withdrawal.accountDetails.name}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.name,
                                      "Account Name"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "Account Name",
                                    copiedField
                                  )}
                                  title="Copy account name"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* Bank Name */}
                            {withdrawal.accountDetails?.bankName && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <CurrencyDollarIcon className="w-4 h-4 mr-2 text-gray-400" />
                                  <span className="text-sm text-gray-500">
                                    {withdrawal.accountDetails.bankName}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.bankName,
                                      "Bank Name"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "Bank Name",
                                    copiedField
                                  )}
                                  title="Copy bank name"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* Account Number */}
                            {withdrawal.accountDetails?.accountNumber && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <ClipboardDocumentIcon className="w-4 h-4 mr-2 text-gray-400" />
                                  <span className="text-sm text-gray-500">
                                    {withdrawal.accountDetails.accountNumber}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.accountNumber,
                                      "Account Number"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "Account Number",
                                    copiedField
                                  )}
                                  title="Copy account number"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* Phone Number (for mobile money) */}
                            {withdrawal.accountDetails?.phoneNumber && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                                  <span className="font-medium">
                                    {withdrawal.accountDetails.phoneNumber}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.phoneNumber,
                                      "Phone Number"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "Phone Number",
                                    copiedField
                                  )}
                                  title="Copy phone number"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* Provider (for mobile money) */}
                            {withdrawal.accountDetails?.provider && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-500">
                                    {withdrawal.accountDetails.provider}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.provider,
                                      "Provider"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "Provider",
                                    copiedField
                                  )}
                                  title="Copy provider"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* PayPal Email */}
                            {withdrawal.accountDetails?.paypalEmail && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-500">
                                    {withdrawal.accountDetails.paypalEmail}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(
                                      withdrawal.accountDetails.paypalEmail,
                                      "PayPal Email"
                                    )
                                  }
                                  className={getCopyButtonStyles(
                                    "PayPal Email",
                                    copiedField
                                  )}
                                  title="Copy PayPal email"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* Show N/A if no account details */}
                            {!withdrawal.accountDetails?.name &&
                              !withdrawal.accountDetails?.bankName &&
                              !withdrawal.accountDetails?.accountNumber &&
                              !withdrawal.accountDetails?.phoneNumber &&
                              !withdrawal.accountDetails?.provider &&
                              !withdrawal.accountDetails?.paypalEmail && (
                                <div className="text-sm text-gray-500">
                                  No account details provided
                                </div>
                              )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(withdrawal.status)}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {withdrawal.createdAt ? (
                            <div>
                              <div>
                                {format(
                                  withdrawal.createdAt.toDate(),
                                  "MMM dd, yyyy"
                                )}
                              </div>
                              <div className="text-xs text-gray-400">
                                {format(withdrawal.createdAt.toDate(), "HH:mm")}
                              </div>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {withdrawal.status === "pending" && (
                              <>
                                <button
                                  onClick={() => handleApprove(withdrawal)}
                                  className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50"
                                  title="Approve"
                                >
                                  <CheckCircleIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleReject(withdrawal)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                                  title="Reject"
                                >
                                  <XCircleIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(withdrawal)}
                              className="text-gray-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {filteredWithdrawals.map((withdrawal) => {
                const user = getUserDetails(withdrawal.userId);
                return (
                  <div
                    key={withdrawal.id}
                    className="bg-white rounded-lg border border-gray-200 p-4 space-y-3"
                  >
                    {/* User Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(withdrawal.status)}
                        {getMethodBadge(withdrawal.method)}
                      </div>
                    </div>

                    {/* Amount and Date */}
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-bold text-gray-900">
                        ${withdrawal.amount?.toFixed(2) || "0.00"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {withdrawal.createdAt
                          ? format(
                              withdrawal.createdAt.toDate(),
                              "MMM dd, yyyy"
                            )
                          : "N/A"}
                      </div>
                    </div>

                    {/* Account Details */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                        Account Details
                      </div>
                      {withdrawal.accountDetails?.name && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.name}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.name,
                                "Account Name"
                              )
                            }
                            className={getCopyButtonStyles(
                              "Account Name",
                              copiedField
                            )}
                            title="Copy account name"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {withdrawal.accountDetails?.bankName && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <CurrencyDollarIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.bankName}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.bankName,
                                "Bank Name"
                              )
                            }
                            className={getCopyButtonStyles(
                              "Bank Name",
                              copiedField
                            )}
                            title="Copy bank name"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {withdrawal.accountDetails?.accountNumber && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <ClipboardDocumentIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.accountNumber}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.accountNumber,
                                "Account Number"
                              )
                            }
                            className={getCopyButtonStyles(
                              "Account Number",
                              copiedField
                            )}
                            title="Copy account number"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {withdrawal.accountDetails?.phoneNumber && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.phoneNumber}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.phoneNumber,
                                "Phone Number"
                              )
                            }
                            className={getCopyButtonStyles(
                              "Phone Number",
                              copiedField
                            )}
                            title="Copy phone number"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {withdrawal.accountDetails?.provider && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.provider}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.provider,
                                "Provider"
                              )
                            }
                            className={getCopyButtonStyles(
                              "Provider",
                              copiedField
                            )}
                            title="Copy provider"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {withdrawal.accountDetails?.paypalEmail && (
                        <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <div className="flex items-center">
                            <span className="text-sm text-gray-900">
                              {withdrawal.accountDetails.paypalEmail}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCopy(
                                withdrawal.accountDetails.paypalEmail,
                                "PayPal Email"
                              )
                            }
                            className={getCopyButtonStyles(
                              "PayPal Email",
                              copiedField
                            )}
                            title="Copy PayPal email"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {!withdrawal.accountDetails?.name &&
                        !withdrawal.accountDetails?.bankName &&
                        !withdrawal.accountDetails?.accountNumber &&
                        !withdrawal.accountDetails?.phoneNumber &&
                        !withdrawal.accountDetails?.provider &&
                        !withdrawal.accountDetails?.paypalEmail && (
                          <div className="text-sm text-gray-500 bg-gray-50 rounded px-3 py-2">
                            No account details provided
                          </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200">
                      {withdrawal.status === "pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(withdrawal)}
                            className="flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100"
                          >
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(withdrawal)}
                            className="flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                          >
                            <XCircleIcon className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(withdrawal)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100"
                      >
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={executeAction}
        title={
          confirmAction === "approve"
            ? "Approve Withdrawal"
            : "Delete Withdrawal"
        }
        message={
          confirmAction === "approve"
            ? `Are you sure you want to approve the withdrawal request for $${selectedWithdrawal?.amount?.toFixed(
                2
              )}?`
            : `Are you sure you want to delete this withdrawal request? This action cannot be undone.`
        }
        confirmText={confirmAction === "approve" ? "Approve" : "Delete"}
        confirmColor={confirmAction === "approve" ? "green" : "red"}
      />

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full mx-4">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Reject Withdrawal
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please provide a reason for rejecting this withdrawal
                        request.
                      </p>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        className="mt-3 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={executeRejection}
                  disabled={!rejectionReason.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectionModal(false);
                    setRejectionReason("");
                    setSelectedWithdrawal(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalsPage;
